import {
    Form,
    FormField,
    FormSection,
    FormType,
    DropdownData,
    FilteredValue,
    InputType,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from 'src/app/common/classes/request-builder';

import { TratamientoManezModel } from 'src/app/models/tratamientos/tratatamiento-manez.model';
import { BoquillaModel } from 'src/app/models/trabajadores/boquillas.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';



export interface Campos {
    id: string;
    nombre: string;
    superficie_sigpac: string;
    superficie_cultivada: string;
    plantas: string;
    marco: string;
    marco_ancho: string;
    marco_largo: string;
    anyo_plantacion: string;
    patron: string;
    id_cultivo: string;
    id_variedad: string;
    cod_provincia: string;
    municipio: string;
    poligono: string;
    numero: string;
    recinto: string;
    id_cliente: string;
    id_usuario: string;
}

export const modoTrabajo: DropdownData<string> = list<string>(['...', null], ['Activado', 'Activado'], ['Desactivado', 'Desactivado']);
export const regulacionDosis: DropdownData<string> = list<string>(['...', null], ['Manual', 'Manual'], ['Automática', 'Automática']);

export interface TrataManezListsDefinition {
    campos: DropdownData<Campos>;
    maquinarias: DropdownData<MaquinariaModel>;
    boquillas: DropdownData<BoquillaModel>;
    productos: DropdownData<ProductosModel>;
    modoTrabajo: DropdownData<string>;
    regulacionDosis: DropdownData<string>;
}

export interface TrataManezFormCallbacks {
    getSelectedMaquinariaId: () => string;
    getCurrentUserId: () => string;
    getSelectedProductIds: () => ReadonlySet<string>;
}

export class TrataManezReactiveFormDefinition {
    public formFields: Form<TratamientoManezModel>;

    public constructor(
        private readonly formType: FormType,
        public model: TratamientoManezModel,
        private readonly lists: TrataManezListsDefinition,
        private readonly callbacks: TrataManezFormCallbacks,
        private unidad: string,
        private readonly userId: string,
        private readonly rol: string
    ) {
        this.formFields = this.buildForm();
    }

    public updateUnidad(unidad: string): void {
        this.unidad = unidad;

        const litrosField = this.findField('litros');

        if (litrosField) {
            litrosField.label = 'Litros/' + this.unidad;
        }
    }

    private buildForm(): Form<TratamientoManezModel> {
        return {
            name: 'tratamientos-manez',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getSections(),
            table: null,
            uploadButton: { isEnabled: false, label: '' },
            signed: false
        };
    }

    private getHeader(): string {
        switch (this.formType) {
            case FormType.EDITION:
                return 'Actualizar Tratamiento';
            case FormType.DUPLICATION:
                return 'Duplicar Tratamiento';
            default:
                return 'Crear Tratamiento';
        }
    }

    private getSections(): FormSection<TratamientoManezModel>[] {
        return [
            {
                name: 'Datos generales',
                fields: this.getMainFields(),
                width: 8,
                isVisible: true,
                order: 1
            },
            {
                name: 'Productos',
                fields: [this.createProductosField()],
                width: 4,
                isVisible: true,
                order: 2
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 3
            }
        ];
    }

    private getMainFields(): FormField<TratamientoManezModel>[] {
        return [
            this.createTextField('n_tratamiento', 'Nº Tratamiento', 1, true, false),
            this.createTextField('nombre', 'Nombre', 2, true, true),
            this.createEmptyField(3),
            this.createMaquinariaField(),
            this.createCampoField(),
            this.createTextField('ancho_calle', 'Ancho de calle', 6, false, true),
            this.createCalendarField(),
            this.createTimeField('hora_inicio', 'Hora Inicio', 8),
            this.createTimeField('hora_fin', 'Hora Fin', 9),
            this.createNumericField('litros', 'Litros/' + this.unidad, 10, true),
            this.createNumericField('marcha', 'Velocidad de avance (km/h)', 11, true),
            this.createNumericField('rpm', 'RPM', 12, false),
            this.createNumericField('presion', 'Presión', 13, false),
            this.createTextField('caudal', 'Caudal (l/min)', 14, false, false),
            this.createBoquillaField(),
            this.createStaticDropdownField('modo_trabajo', 'Modo trabajo', this.lists.modoTrabajo, 16, true),
            this.createStaticDropdownField('regulacion_dosis', 'Regulación dosis', this.lists.regulacionDosis, 17, true),
            this.createTextField('estado_tratamiento', 'Estado tratamiento', 18, false, false, 'Preparado')
        ];
    }

    private getHiddenFields(): FormField<TratamientoManezModel>[] {
        return [
            this.createHiddenField('id', 1, undefined, false),
            this.createHiddenField('id_usuario', 2, () => this.model.id_usuario ?? this.callbacks.getCurrentUserId(), true)
        ];
    }

    private createMaquinariaField(): FormField<TratamientoManezModel> {
        return {
            name: 'id_maquinaria',
            vinculatedFields: undefined,
            label: 'Maquinaria',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.maquinarias,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    hasMaxHeight: true,
                    filter: (it: FilteredValue<TratamientoManezModel>): boolean => {
                        const value = it.value as unknown as MaquinariaModel | null;

                        return value === null || value.id_usuario === this.userId || this.rol === 'admin';
                    }
                }
            },
            width: 4,
            conditions: this.getConditions(true),
            order: 4
        };
    }

    private createCampoField(): FormField<TratamientoManezModel> {
        return {
            name: 'id_campo',
            vinculatedFields: undefined,
            label: 'Campo',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.campos,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    hasMaxHeight: true,
                    filter: (it: FilteredValue<TratamientoManezModel>): boolean => {
                        const value = it.value as unknown as Campos | null;

                        return value === null || value.id_usuario === this.userId || this.rol === 'admin';
                    }
                }
            },
            width: 4,
            conditions: this.getConditions(true),
            order: 5
        };
    }

    private createBoquillaField(): FormField<TratamientoManezModel> {
        return {
            name: 'id_boquilla',
            dependencies: [
                {
                    source: 'id_maquinaria',
                    target: 'id_boquilla',
                    kind: 'filter'
                }
            ],
            vinculatedFields: undefined,
            label: 'Boquilla',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.boquillas,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    hasMaxHeight: true,
                    filter: (it: FilteredValue<TratamientoManezModel>): boolean => {
                        const selectedMaquinariaId = this.callbacks.getSelectedMaquinariaId();
                        const value = it.value as unknown as BoquillaModel | null;

                        return value === null || (selectedMaquinariaId !== '' && value.id_maquinaria?.toString() === selectedMaquinariaId);
                    }
                }
            },
            width: 4,
            conditions: this.getConditions(false),
            dependencyBehaviour: {
                clearOnParentChange: true,
                filterLocally: true
            },
            order: 15
        };
    }

    private createProductosField(): FormField<TratamientoManezModel> {
        return {
            name: 'productos_repeatable',
            vinculatedFields: undefined,
            label: 'Productos',
            input: {
                type: InputType.REPEATABLE_GROUP,
                isEditable: true,
                repeatableGroupConfiguration: {
                    rows: 7,
                    addButtonLabel: 'Añadir producto',
                    bindings: {
                        producto: 'ids_productos',
                        dosis: 'cantidades_real'
                    },
                    serialization: {
                        separator: ';'
                    },
                    forceStackedLayout: true,
                    subFields: [
                        {
                            key: 'producto',
                            label: 'Nombre',
                            input: {
                                type: InputType.DROPDOWN_SEARCH,
                                isEditable: true,
                                data: this.lists.productos,
                                dropdownConfiguration: {
                                    valuePrimaryKey: 'id',
                                    controlValueKey: 'id',
                                    hasMaxHeight: true,
                                    filter: (it: FilteredValue<unknown>): boolean => {
                                        const value = (it.raw ?? it.value) as ProductosModel | null;

                                        if (value === null) {
                                            return true;
                                        }

                                        const selectedProductIds = this.callbacks.getSelectedProductIds();
                                        const productIds = this.getComparableProductIds(value);

                                        return productIds.some((id) => selectedProductIds.has(id))
                                            || value.id_usuario === this.userId
                                            || this.rol === 'admin';
                                    }
                                }
                            }
                        },
                        {
                            key: 'dosis',
                            label: 'Dosis (%)',
                            input: {
                                type: InputType.NUMERIC,
                                isEditable: true
                            }
                        },
                        {
                            key: 'unidad',
                            label: 'Unidades',
                            input: {
                                type: InputType.ONLY_TEXT,
                                isEditable: false
                            },
                            dependsOn: 'producto',
                            onDependencyChange: (ctx) => {
                                const producto = ctx.resolveEntity?.<ProductosModel>('producto');

                                return { unidad: producto?.uds_medida ?? producto?.uds ?? producto?.unidades ?? '' };
                            }
                        }
                    ],
                    legacyAggregateSerialization: {
                        targets: {
                            producto: 'ids_productos',
                            dosis: 'cantidades_real'
                        },
                        separator: ';'
                    }
                }
            },
            width: 12,
            conditions: this.getConditions(false),
            order: 1
        };
    }

    private getComparableProductIds(product: ProductosModel): string[] {
        const ids = new Set<string>();
        const productId = String(product.id ?? '').trim();

        if (productId !== '') {
            ids.add(productId);

            if (/^\d+$/.test(productId)) {
                ids.add(String(Number(productId)));
            }
        }

        return Array.from(ids);
    }


    private createStaticDropdownField(
        name: keyof TratamientoManezModel & string,
        label: string,
        data: DropdownData<string>,
        order: number,
        required: boolean
    ): FormField<TratamientoManezModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: InputType.DROPDOWN,
                isEditable: true,
                data
            },
            width: 4,
            conditions: this.getConditions(required),
            order
        };
    }

    private createCalendarField(): FormField<TratamientoManezModel> {
        return {
            name: 'fecha_inicio',
            vinculatedFields: undefined,
            label: 'Fecha del Tratamiento',
            input: {
                type: InputType.CALENDAR,
                isEditable: true
            },
            width: 4,
            conditions: this.getConditions(true),
            order: 7
        };
    }

    private createTimeField(
        name: keyof TratamientoManezModel & string,
        label: string,
        order: number
    ): FormField<TratamientoManezModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: InputType.TIME,
                isEditable: true
            },
            width: 4,
            conditions: this.getConditions(false),
            order
        };
    }

    private createTextField(
        name: keyof TratamientoManezModel & string,
        label: string,
        order: number,
        required: boolean,
        editable: boolean,
        defaultValue?: string | number | Date | null
    ): FormField<TratamientoManezModel> {
        const input = {
            type: InputType.TEXT,
            isEditable: editable
        } as FormField<TratamientoManezModel>['input'];

        if (defaultValue !== undefined) {
            input.defaultValue = defaultValue;
        }

        return {
            name,
            vinculatedFields: undefined,
            label,
            input,
            width: 4,
            conditions: this.getConditions(required),
            order
        };
    }

    private createNumericField(
        name: keyof TratamientoManezModel & string,
        label: string,
        order: number,
        required: boolean
    ): FormField<TratamientoManezModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: InputType.NUMERIC,
                isEditable: true
            },
            width: 4,
            conditions: this.getConditions(required),
            order
        };
    }

    private createEmptyField(order: number): FormField<TratamientoManezModel> {
        return {
            name: 'empty_' + order,
            vinculatedFields: undefined,
            label: '',
            input: {
                type: InputType.EMPTY,
                isEditable: false
            },
            width: 4,
            conditions: this.getConditions(false),
            order
        };
    }

    private createHiddenField(
        name: keyof TratamientoManezModel & string,
        order: number,
        updateValue?: () => string | TratamientoManezModel | null | undefined,
        isRememberedOnDuplicate: boolean = true
    ): FormField<TratamientoManezModel> {
        const field: FormField<TratamientoManezModel> = {
            name,
            vinculatedFields: undefined,
            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: false
            },
            width: 1,
            conditions: {
                ...this.getConditions(false),
                isVisible: true,
                isRememberedOnDuplicate
            },
            order
        };

        if (updateValue) {
            field.updateValue = updateValue;
        }

        return field;
    }

    private getConditions(required: boolean) {
        return {
            isRememberedOnDuplicate: false,
            isRequired: required,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true
        };
    }

    private findField(name: string): FormField<TratamientoManezModel> | undefined {
        return this.formFields.sections
            .flatMap((section) => section.fields)
            .find((field) => field.name === name);
    }
}
